import { Baby, Clock3, Droplets, Milk, Moon, Plus, Settings2 } from 'lucide-react';

import { buildStats } from '../lib/stats.js';
import { babyAge, eventSummary, formatClock, formatDuration } from '../lib/format.js';
import GestureAction from './GestureAction.jsx';
import Timeline from './Timeline.jsx';

export default function HomePage({
  profile,
  events,
  activeSleep,
  now,
  gesture,
  onGestureChange,
  onGestureComplete,
  onOpenForm,
  onToggleSleep,
  onEdit,
  onDelete,
  onOpenSettings,
  busy,
}) {
  const stats = buildStats(events, 1, now);
  const lastFeed = events.find((event) => event.kind === 'feed');
  const lastDiaper = events.find((event) => event.kind === 'poop' || event.kind === 'pee');
  const activeMinutes = activeSleep ? Math.floor((now - new Date(activeSleep.startedAt)) / 60000) : 0;

  return (
    <div className="page page--home">
      <header className="topbar">
        <div className="baby-identity">
          <span className="baby-avatar" aria-hidden="true">{profile.babyName.slice(0, 1)}</span>
          <div>
            <h1>{profile.babyName}</h1>
            <p>{babyAge(profile.birthDate, now)} · 今日照护</p>
          </div>
        </div>
        <button type="button" className="icon-button" onClick={onOpenSettings} aria-label="宝宝资料与设置">
          <Settings2 size={21} />
        </button>
      </header>

      {activeSleep ? (
        <button type="button" className="active-sleep-banner" onClick={onToggleSleep} disabled={busy}>
          <span className="active-sleep-banner__pulse" />
          <span><strong>正在睡眠</strong><small>{formatDuration(activeMinutes)} · {formatClock(activeSleep.startedAt)} 入睡</small></span>
          <span>轻点结束</span>
        </button>
      ) : null}

      <section className="today-strip" aria-label="今日概览">
        <div>
          <span>上次吃奶</span>
          <strong>{lastFeed ? formatClock(lastFeed.startedAt) : '—'}</strong>
          <small>{lastFeed ? eventSummary(lastFeed).split(' · ').slice(1).join(' · ') : '暂无记录'}</small>
        </div>
        <div>
          <span>今日便尿</span>
          <strong>{stats.diaper.poopCount + stats.diaper.peeCount}</strong>
          <small>拉 {stats.diaper.poopCount} · 尿 {stats.diaper.peeCount}</small>
        </div>
        <div>
          <span>今日睡眠</span>
          <strong>{stats.sleep.minutes ? `${Math.floor(stats.sleep.minutes / 60)}h ${stats.sleep.minutes % 60}m` : '—'}</strong>
          <small>{stats.sleep.sessions ? `${stats.sleep.sessions} 次完整记录` : '暂无完整记录'}</small>
        </div>
      </section>

      <section className="action-section">
        <div className="section-heading section-heading--compact">
          <div><span className="eyebrow">快速记录</span><h2>现在发生了什么？</h2></div>
          <button type="button" className="text-button" onClick={() => onOpenForm('feed')}><Plus size={16} />补录</button>
        </div>

        <div className="quick-grid">
          <GestureAction
            kind="feed"
            label="喂养"
            hint="点击记录，长按调整"
            icon={Milk}
            disabled={busy}
            onTap={() => onGestureComplete({ kind: 'feed', selected: 'formula', amount: profile.quickFormulaInitial ?? 20 })}
            onGestureChange={onGestureChange}
            onComplete={onGestureComplete}
            quickSettings={{
              step: profile.quickFeedStep,
              nursingStep: profile.quickNursingStep,
              intervalMs: profile.quickHoldIntervalMs,
              breastmilkInitial: profile.quickBreastmilkInitial,
              formulaInitial: profile.quickFormulaInitial,
              nursingInitial: profile.quickNursingInitial,
              max: 500,
              nursingMax: 180,
            }}
          />
          <button type="button" className={`quick-action quick-action--sleep ${activeSleep ? 'is-active' : ''}`} onClick={onToggleSleep} disabled={busy} aria-pressed={Boolean(activeSleep)}>
            <span className="quick-action__icon" aria-hidden="true"><Moon size={28} strokeWidth={2.2} /></span>
            <span className="quick-action__copy">
              <strong>{activeSleep ? '结束睡眠' : '开始睡眠'}</strong>
              <small>{activeSleep ? '睡眠中 · 轻点结束' : '点击开始计时'}</small>
            </span>
          </button>
          <GestureAction
            kind="poop"
            label="大便"
            hint="点击记录，长按调整"
            icon={Baby}
            disabled={busy}
            onTap={() => onGestureComplete({ kind: 'poop', amount: 3 })}
            onGestureChange={onGestureChange}
            onComplete={onGestureComplete}
          />
          <GestureAction
            kind="pee"
            label="小便"
            hint="点击记录，长按调整"
            icon={Droplets}
            disabled={busy}
            onTap={() => onGestureComplete({ kind: 'pee', amount: 2 })}
            onGestureChange={onGestureChange}
            onComplete={onGestureComplete}
          />
        </div>
        <p className="gesture-note"><Clock3 size={15} />喂养、大便、小便可长按快速记录，轻点可直接记录。</p>
      </section>

      <section className="content-section">
        <div className="section-heading section-heading--compact">
          <div><span className="eyebrow">时间线</span><h2>最近记录</h2></div>
          {lastDiaper ? <span className="section-meta">更新于 {formatClock(events[0]?.startedAt)}</span> : null}
        </div>
        <Timeline events={events} limit={6} onEdit={onEdit} onDelete={onDelete} emptyText="还没有照护记录" emptyAction={{ label: '从吃奶开始记录', onClick: () => onOpenForm('feed') }} />
      </section>
    </div>
  );
}
